import { MAX_PERSISTED_CANDLES } from './candleConstants';
import { INDICATOR_OPTIONS } from './indicatorRegistry';
import { createDefaultBasicStudySettingsMap } from './overlays/basicStudies';
import { DEFAULT_BOT_STRATEGY_KEY } from './strategyPlugins';
import type { OverlaySelection, OverlaySettings } from './types';

function currentUtcDateAsDayMonthYearDate(): string {
  const now = new Date();
  return `${String(now.getUTCDate()).padStart(2, '0')}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCFullYear())}`;
}

export const DEFAULT_OVERLAY_SETTINGS: OverlaySettings = {
  chart: {
    autoFit: true,
    scalePriceChartOnly: true,
    invertScale: false,
    scaleMode: 'regular',
    labels: {
      crosshairLabels: true,
      scaleTicks: false,
    },
    lines: {
      horizontalGrid: true,
      verticalGrid: true,
      crosshairLines: true,
    },
    symbol: {
      colorBarsBasedOnPreviousClose: false,
      showBody: true,
      showBorders: true,
      showWick: true,
      upBodyColor: '#22c55e',
      downBodyColor: '#ef4444',
      upBorderColor: '#22c55e',
      downBorderColor: '#ef4444',
      upWickColor: '#22c55e',
      downWickColor: '#ef4444',
      precision: 'default',
      timezone: 'UTC',
    },
    statusLine: {
      showSymbol: true,
      showInterval: true,
      showExchange: true,
      showOHLC: true,
      showChange: true,
      showVolume: false,
      showLastDayChange: false,
      showPaperSummary: false,
      showIndicatorArguments: false,
      backgroundOpacity: 10,
    },
    canvas: {
      backgroundType: 'solid',
      backgroundColor: '#0f172a',
      backgroundTopColor: '#111827',
      backgroundBottomColor: '#0f172a',
      textColor: '#e2e8f0',
      gridColor: '#1e293b',
      crosshairColor: '#94a3b8',
      margins: {
        top: 10,
        bottom: 8,
        right: 5,
      },
    },
    trading: {
      showPositionsAndOrders: true,
      showExecutionMarks: true,
      showExecutionLabels: true,
      showSeriesLabels: true,
    },
    alerts: {
      showAlertLines: true,
      onlyActiveAlerts: true,
      autoHideToasts: true,
      alertSoundType: 'chime',
      alertSoundVolume: 70,
    },
    events: {
      showIdeas: false,
      showSessionBreaks: false,
      showEconomicEvents: false,
      showLatestNews: false,
      showNewsNotifications: false,
    },
  },
  volume: {
    inputs: {},
    style: {
      upColor: '#22c55e',
      downColor: '#ef4444',
      opacity: 1,
    },
    visibility: {
      showHistogram: true,
    },
  },
  rsi: {
    inputs: {
      length: 14,
    },
    style: {
      lineColor: '#a78bfa',
      overboughtColor: '#ef4444',
      oversoldColor: '#22c55e',
      lineWidth: 2,
    },
    visibility: {
      showLine: true,
      showZones: true,
      showSeriesLabels: true,
    },
  },
  macd: {
    inputs: {
      fastLength: 12,
      slowLength: 26,
      signalLength: 9,
    },
    style: {
      macdColor: '#38bdf8',
      signalColor: '#f59e0b',
      histogramPositiveColor: '#22c55e',
      histogramNegativeColor: '#ef4444',
      lineWidth: 2,
    },
    visibility: {
      showMacdLine: true,
      showSignalLine: true,
      showHistogram: true,
      showSeriesLabels: true,
    },
  },
  cmVixFixUcsSqueeze: {
    inputs: {
      useAlternativeTimeframe: false,
      alternativeTimeframe: '1h',
      pd: 22,
      bbl: 20,
      mult: 2,
      lb: 50,
      ph: 0.85,
      pl: 1.01,
      hp: false,
      sd: false,
      scale: 1,
      length: 20,
      multBB: 2,
      multKC: 1.5,
      smooth: 20,
      usebbr: true,
      useHAC: true,
      useTrueRange: true,
    },
    style: {
      wvfSpikeColor: '#00ff00',
      wvfBaseColor: '#808080',
      momentumPositiveRisingColor: '#008000',
      momentumPositiveFallingColor: '#0000ff',
      momentumNegativeFallingColor: '#ff0000',
      momentumNegativeRisingColor: '#ffa500',
      squeezeOnColor: '#ff0000',
      squeezeOffColor: '#00ff00',
      noSqueezeColor: '#0000ff',
    },
    visibility: {
      showWvfHistogram: true,
      showMomentumHistogram: true,
      showSqueezeStateLine: true,
      showSeriesLabels: false,
    },
  },
  movingAverage: {
    inputs: {
      period: 20,
      maType: 'ma',
      source: 'close',
      offsetPercent: 0,
    },
    style: {
      color: '#38bdf8',
      lineWidth: 2,
      lineStyle: 'solid',
    },
    visibility: {
      showLine: true,
    },
  },
  murreyMathLinesV6: {
    inputs: {
      useAlternativeTimeframe: false,
      alternativeTimeframe: '1h',
      frameSize: 64,
      frameMultiplier: 1.5,
      sourceMode: 'highLow',
      addExtendedLines: true,
      extraLinesCount: 4,
      lockToSessionStart: false,
      tradingSession: '0930-1600',
    },
    style: {
      majorLineColor: '#ef4444',
      rangeLineColor: '#38bdf8',
      pivotLineColor: '#f8fafc',
      weakLineColor: '#94a3b8',
      extendedLineColor: '#22c55e',
      lineWidth: 1,
      lineStyle: 'dotted',
      zoneOpacity: 16,
    },
    visibility: {
      showLines: true,
      showZones: true,
      showInfo: true,
      showSeriesLabels: true,
    },
  },
  bollingerBands: {
    inputs: {
      length: 20,
      multiplier: 2,
      source: 'close',
      basisType: 'sma',
      offset: 0,
    },
    style: {
      upperColor: '#2962ff',
      basisColor: '#ff9800',
      lowerColor: '#2962ff',
      fillColor: '#2962ff',
      upperLineWidth: 1,
      basisLineWidth: 1,
      lowerLineWidth: 1,
      upperLineStyle: 'solid',
      basisLineStyle: 'solid',
      lowerLineStyle: 'solid',
      fillOpacity: 14,
    },
    visibility: {
      showUpper: true,
      showBasis: true,
      showLower: true,
      showFill: true,
    },
  },
  ichimokuCloud: {
    inputs: {
      tenkanPeriod: 9,
      kijunPeriod: 26,
      senkouBPeriod: 52,
      displacement: 26,
    },
    style: {
      tenkanColor: '#e11d48',
      kijunColor: '#2563eb',
      senkouAColor: '#16a34a',
      senkouBColor: '#dc2626',
      chikouColor: '#22c55e',
      bullishFillColor: '#16a34a',
      bearishFillColor: '#dc2626',
      tenkanLineWidth: 1,
      kijunLineWidth: 1,
      senkouALineWidth: 1,
      senkouBLineWidth: 1,
      chikouLineWidth: 1,
      tenkanLineStyle: 'solid',
      kijunLineStyle: 'solid',
      senkouALineStyle: 'solid',
      senkouBLineStyle: 'solid',
      chikouLineStyle: 'solid',
      fillOpacity: 18,
    },
    visibility: {
      showTenkan: true,
      showKijun: true,
      showSenkouA: true,
      showSenkouB: true,
      showChikou: true,
      showCloud: true,
    },
  },
  candleHelper: {
    inputs: {
      detectBullishEngulfing: true,
      detectBearishEngulfing: true,
      detectDoji: true,
      dojiBodyRatioPercent: 10,
    },
    style: {
      bullishEngulfingColor: '#22c55e',
      bearishEngulfingColor: '#ef4444',
      dojiColor: '#f59e0b',
      markerSize: 2,
    },
    visibility: {
      showMarkers: true,
      showLabels: true,
    },
  },
  traderProV3: {
    inputs: {
      stopLossRatio: 1.7,
    },
    style: {
      entryColor: '#22c55e',
      tp1Color: '#38bdf8',
      tp2Color: '#60a5fa',
      stopColor: '#f97316',
      resistanceColor: '#ef4444',
      midpointColor: '#f8fafc',
      supportColor: '#22c55e',
      tradeLineWidth: 2,
      murreyLineWidth: 1,
      murreyLineStyle: 'dotted',
    },
    visibility: {
      showTradeLevels: true,
      showMurreyLevels: true,
      showInfoBadge: true,
      showSeriesLabels: true,
    },
  },
    traderPro17: {
      inputs: {
        enable200Dma: false,
        enableAdvancedTrading: true,
        enableNoobProtection: false,
        useAlternativeTimeframe: false,
        alternativeTimeframe: '1h',
        rangeScale: 1,
      },
    style: {
      resistanceColor: '#ef4444',
      neutralColor: '#3b82f6',
      supportColor: '#22c55e',
      extensionColor: '#22c55e',
      pullbackTextColor: '#ef4444',
      algoTargetColor: '#f97316',
    },
    visibility: {
      showLevelGuides: true,
      showPullbackHints: true,
      showAlignmentHints: true,
      showAlgoTargets: true,
      horizontalOffset: 0,
      showInfoBadge: true,
    },
  },
    traderPro17Strategy: {
      inputs: {
        timeframe: '15m',
        supportMode: 'support100',
        rangeScale: 1,
        useOneMinuteStopRefinement: true,
      requireMa200RisingForEntries: true,
      entryBodyPctMin: -1,
      entryClosePosMin: 0,
      entryDistAboveMa200PctMin: -10,
      makerFeePercent: 0.05,
      takerFeePercent: 0.05,
      stopLossPercent: 1.1,
      breakEvenTriggerR: 0.6,
      breakEvenOffsetPercent: 0.02,
      atrPeriod: 28,
      atrMultiplier: 2.6,
      queueExpiryHours: 36,
      trailingMode: 'smart',
      smartTrailArmR: 0.6,
      smartTrailLockR: 0.3,
      smartTrailMaxGivebackR: 1.4,
      smartTrailAtrMultiplier: 3,
      smartTrailTightenR: 1.1,
      smartTrailTightenAtrMultiplier: 0.8,
      maxHistoryCandles: MAX_PERSISTED_CANDLES,
      refreshStartDate: currentUtcDateAsDayMonthYearDate(),
    },
    style: {
      buyMarkerColor: '#eab308',
      takeProfitColor: '#22c55e',
      stopLossColor: '#ef4444',
    },
    visibility: {
      showMarkers: true,
      showActiveLevels: true,
      useDottedLines: false,
      showInfoBadge: true,
      showSeriesLabels: true,
    },
  },
  openCode5mStrategy: {
    inputs: {
      timeframe: '5m',
      makerFeePercent: 0.05,
      takerFeePercent: 0.05,
      allowLongs: false,
      allowShorts: true,
      fastEmaPeriod: 34,
      slowEmaPeriod: 144,
      rsiPeriod: 21,
      rsiLongMin: 54,
      rsiShortMax: 40,
      atrPeriod: 14,
      minTrendStrengthPercent: 0.3,
      minRiskPercent: 0.8,
      stopLossAtrMultiplier: 2.8,
      takeProfitR: 1.2,
      trailingActivationR: 2,
      breakEvenR: 2,
      trailingAtrMultiplier: 3,
      maxHoldCandles: 144,
      cooldownCandles: 0,
      forceEntryAfterCandles: 120,
      maxHistoryCandles: MAX_PERSISTED_CANDLES,
    },
    style: {
      longEntryColor: '#22c55e',
      shortEntryColor: '#ef4444',
      takeProfitColor: '#14b8a6',
      stopLossColor: '#f97316',
    },
    visibility: {
      showMarkers: true,
      showActiveLevels: true,
      useDottedLines: false,
      showInfoBadge: true,
      showSeriesLabels: true,
    },
  },
  colorProTradingSuiteV31Strategy: {
    inputs: {
      timeframe: '1h',
      useOneMinuteStopRefinement: true,
      skipStepOnRedClose: false,
      requireMa200AboveLastCandle: false,
      stopBufferPercent: 0,
      makerFeePercent: 0.05,
      takerFeePercent: 0.05,
      runnerTrailPercent: 1.2,
      maxHistoryCandles: MAX_PERSISTED_CANDLES,
    },
    style: {
      buyMarkerColor: '#eab308',
      takeProfitColor: '#22c55e',
      stopLossColor: '#ef4444',
    },
    visibility: {
      showMarkers: true,
      showActiveLevels: true,
      useDottedLines: false,
      showInfoBadge: true,
      showSeriesLabels: true,
    },
  },
  botTrades: {
    inputs: {
      strategyKey: DEFAULT_BOT_STRATEGY_KEY,
    },
    style: {
      entryColor: '#22c55e',
      exitColor: '#ef4444',
      paperColor: '#f59e0b',
    },
    visibility: {
      showCompletedTrades: true,
      showActiveUpdates: true,
      showLabels: true,
    },
  },
  colorProTradingSuiteV3: {
    inputs: {
      rsiLength: 12,
      stochLength: 14,
      kSmoothing: 1,
      dSmoothing: 3,
      bollingerLength: 20,
      bollingerMultiplier: 2,
      useBreakoutVolumeFilter: true,
      breakoutVolumeMultiplier: 1.4,
    },
    style: {
      bollingerColor: '#2f7578',
      basisColor: '#6d2ab3',
      dailyResistanceColor: '#e84b30',
      dailySupportColor: '#4ea33d',
      weeklyResistanceColor: '#b52c1a',
      weeklySupportColor: '#2f7a22',
      pivotColor: '#d98f28',
      bearishPivotSignalColor: '#eb8c1d',
      confirmedReversalSignalColor: '#1d4ed8',
      bullishRegularSignalColor: '#16a34a',
      bearishRegularSignalColor: '#dc2626',
      bullishHiddenSignalColor: '#84cc16',
      bearishHiddenSignalColor: '#c026d3',
    },
    visibility: {
      showBollinger: true,
      showDailyLevels: true,
      showWeeklyLevels: true,
      showSignals: true,
      showCandleColors: true,
      showInfoBadge: true,
    },
  },
  colorProTradingSuiteV31: {
    inputs: {
      rsiLength: 12,
      stochLength: 14,
      kSmoothing: 1,
      dSmoothing: 3,
      bollingerLength: 20,
      bollingerMultiplier: 2,
      useBreakoutVolumeFilter: true,
      breakoutVolumeMultiplier: 1.4,
      allowUpperBandBridgeYellow: true,
    },
    style: {
      bollingerColor: '#2f7578',
      basisColor: '#6d2ab3',
      dailyResistanceColor: '#e84b30',
      dailySupportColor: '#4ea33d',
      weeklyResistanceColor: '#b52c1a',
      weeklySupportColor: '#2f7a22',
      pivotColor: '#d98f28',
      bearishPivotSignalColor: '#eb8c1d',
      confirmedReversalSignalColor: '#1d4ed8',
      bullishRegularSignalColor: '#16a34a',
      bearishRegularSignalColor: '#dc2626',
      bullishHiddenSignalColor: '#84cc16',
      bearishHiddenSignalColor: '#c026d3',
    },
    visibility: {
      showBollinger: true,
      showDailyLevels: true,
      showWeeklyLevels: true,
      showSignals: true,
      showCandleColors: true,
      matchEntireCandleToColor: true,
      showInfoBadge: true,
    },
  },
  basicStudies: createDefaultBasicStudySettingsMap(),
};

export const DEFAULT_OVERLAYS: OverlaySelection = Object.fromEntries(
  INDICATOR_OPTIONS.map((option) => [option.key, option.defaultEnabled === true]),
) as OverlaySelection;
